import React, { useContext, useEffect, useRef, useState } from "react";
import APIContext from "../hooks/API";
import { useNavigate } from "react-router-dom";
import { T } from "./traks";

export const LoginContext = React.createContext();

export const useLogin = () => {
	const [agentId, setAgentId] = useState("");
	const [password, setPassword] = useState("");
	const [doLogin, setDoLogin] = useState(false);
	const [MFA, setMFA] = useState("");
	const [user, setUser] = useState();
	const [useMFA, setUseMFA] = useState();
	const { post, setAuthToken, authToken, get } = useContext(APIContext);
	useEffect(() => {
		if (doLogin) {
			post({
				path: "/login",
				args: { agentId, password, MFA, dryrun: false },
			})
				.then((e) => {
					setUseMFA(e?.useMFA);
					setAuthToken(e?.authToken);
				})
				.catch((e) => {
					console.log("login error:", e);
					setAuthToken();
				})
				.finally(() => setDoLogin(false));
		}
	}, [doLogin, agentId, password, MFA, post, setAuthToken, useMFA, setUseMFA]);

	useEffect(() => {
		if (authToken && agentId && !user) {
			get({ path: `/user/${agentId}` }).then((r) => setUser(r));
		} else if (authToken && !agentId && !user) {
			console.log("Got no agentId or user but has authToken");
			get({ path: "/user" }).then((r) => {
				if (!r) {
					setAuthToken();
				} else {
					setAgentId(r.agentId);
				}
			});
		}
	}, [authToken, get, setAgentId, user, agentId, setAuthToken]);

	return {
		agentId,
		setAgentId,
		user,
		password,
		setPassword,
		signIn: () => setDoLogin(true),
		signOut: () => {
			setAuthToken();
			setUser();
		},
		MFA,
		setMFA,
		useMFA,
	};
};

const Login = () => {
	const {
		setAgentId,
		setPassword,
		agentId,
		password,
		signIn,
		setMFA,
		user,
		useMFA,
	} = useContext(LoginContext);
	const [passwordIsValid, setPasswordIsValid] = useState(false);
	const navigate = useNavigate();
	useEffect(() => {
		if (!!user) {
			navigate("/home");
		}
	}, [navigate, user]);

	return (
		<div className="login-wrapper">
			<div className="login-card">
				<h2>
					<T>Log in</T>
				</h2>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						if (passwordIsValid) signIn();
					}}
					autoComplete="on"
				>
					<div className="mb-3">
						<label htmlFor="login-username" className="form-label">
							<T>User ID</T>
						</label>
						<input
							id="login-username"
							name="username"
							type="text"
							autoComplete="username"
							placeholder="Enter your user ID"
							className="form-control"
							value={agentId}
							onChange={(e) => setAgentId(e.target.value)}
						/>
					</div>
					<PasswordValidation
						{...{
							agentId,
							password,
							setPassword,
							passwordIsValid,
							setPasswordIsValid,
							signIn,
						}}
					/>
					<button
						className="btn btn-primary mt-2"
						type="submit"
						disabled={!passwordIsValid}
					>
						<T>Sign in</T>
					</button>
				</form>
				{useMFA && (
					<MFAInput
						onSubmit={(e) => {
							setMFA(e);
							signIn();
						}}
						title="Enter verification code"
					/>
				)}
			</div>
		</div>
	);
};

export const MFAInput = ({ onSubmit, title }) => {
	const [value, setValue] = useState(["", "", "", "", "", ""]);
	const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

	useEffect(() => {
		refs[0].current?.focus();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const handleInput = (e, idx) => {
		const char = e.target.value.replace(/\D/g, "").slice(-1);
		const next = [...value];
		next[idx] = char;
		setValue(next);

		if (!char) return;

		if (idx < 5) {
			refs[idx + 1].current?.focus();
		} else {
			onSubmit(next.join(""));
		}
	};

	const handleKeyDown = (e, idx) => {
		if (e.key === "Backspace" && !value[idx] && idx > 0) {
			refs[idx - 1].current?.focus();
		}
	};

	const handlePaste = (e) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
		if (!pasted) return;
		const next = [...value];
		for (let i = 0; i < pasted.length; i++) {
			next[i] = pasted[i];
		}
		setValue(next);
		if (pasted.length === 6) {
			onSubmit(next.join(""));
		} else {
			refs[Math.min(pasted.length, 5)].current?.focus();
		}
	};

	return (
		<div className="mfa-section">
			<h4>{title}</h4>
			<div className="mfa-digits" onPaste={handlePaste}>
				{refs.map((ref, idx) => (
					<input
						key={idx}
						ref={ref}
						type="text"
						inputMode="numeric"
						pattern="[0-9]*"
						maxLength={1}
						autoComplete={idx === 0 ? "one-time-code" : "off"}
						aria-label={`Digit ${idx + 1} of 6`}
						className="mfa-digit"
						value={value[idx]}
						onChange={(e) => handleInput(e, idx)}
						onKeyDown={(e) => handleKeyDown(e, idx)}
					/>
				))}
			</div>
		</div>
	);
};

// Keep old export name for backwards compatibility
export const MFA = MFAInput;

export const PasswordValidation = ({
	agentId,
	password,
	setPassword,
	passwordIsValid,
	setPasswordIsValid,
	signIn,
}) => {
	const { post } = useContext(APIContext);

	useEffect(() => {
		if (password.length) {
			post({
				path: `/login`,
				args: { agentId, password, MFA: "", dryrun: true },
			}).then((e) => {
				if (e?.password) {
					setPasswordIsValid(true);
				} else {
					setPasswordIsValid(false);
				}
			});
		}
	}, [password, setPasswordIsValid, post, agentId]);

	return (
		<div className="mb-3">
			<label htmlFor="login-password" className="form-label">
				<T>Password</T>
			</label>
			<input
				id="login-password"
				name="password"
				type="password"
				autoComplete="current-password"
				placeholder="Enter your password"
				className={`form-control ${
					!password.length ? "" : passwordIsValid ? "is-valid" : "is-invalid"
				}`}
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				onKeyDown={(e) =>
					passwordIsValid && e.key === "Enter" ? signIn() : null
				}
			/>
			<div className="invalid-feedback">Invalid password</div>
		</div>
	);
};

export default Login;
